import type { PageDetailResponse } from "contracts/api/dashboard/page-detail.responses";
import { Show } from "solid-js";
import { formatDate, relativeAge } from "../../ui/format";
import { rawPagePath } from "../pages/boundaries/page-label.parser";
import { Fact } from "./Fact";

function ScrapeProofPanel(props: {
  proof: NonNullable<PageDetailResponse["scrapeProof"]>;
}) {
  const proof = props.proof;
  return (
    <section>
      <dl class="scan-facts">
        <Fact label="Capture" value={proof.captureVersion} mono />
        <Fact label="HTTP" value={String(proof.httpStatus)} mono />
        <Fact label="Resolved" value={proof.resolvedUrl ?? "--"} mono />
        <Fact label="Captured" value={formatDate(proof.capturedAt)} />
      </dl>
      <div class="scan-subhead">Summary</div>
      <dl class="scan-facts">
        <Fact
          label="Rendered bytes"
          value={proof.summary.renderedBytes?.toString() ?? "--"}
          mono
        />
        <Fact
          label="Source bytes"
          value={proof.summary.sourceBytes?.toString() ?? "--"}
          mono
        />
        <Fact
          label="JSON-LD"
          value={`${proof.summary.jsonLdCount} (${proof.summary.jsonLdParseErrors} err)`}
          mono
        />
        <Fact
          label="Text length"
          value={String(proof.summary.textLength)}
          mono
        />
        <Fact label="Links" value={String(proof.summary.linkCount)} mono />
        <Fact label="Forms" value={String(proof.summary.formCount)} mono />
        <Fact label="Buttons" value={String(proof.summary.buttonCount)} mono />
        <Fact
          label="Next hydration"
          value={proof.summary.nextHydrationDetected ? "yes" : "no"}
        />
      </dl>
      <div class="scan-subhead">Artifacts</div>
      <ul class="scan-proof-list">
        {proof.artifacts.map((artifact) => (
          <li class="mono">
            {artifact.kind} / {artifact.byteLength}b /{" "}
            {artifact.sha256.slice(0, 12)}...
          </li>
        ))}
      </ul>
      <Show when={proof.samples.renderedText}>
        <div class="scan-subhead">Rendered text sample</div>
        <p class="scan-lede">{proof.samples.renderedText}</p>
      </Show>
    </section>
  );
}

export function PageTechnicalDetail(props: { value: PageDetailResponse }) {
  const page = () => props.value.page;
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Technical detail</span>
          <h2>Capture and run metadata</h2>
          <p class="scan-muted">
            Operational identifiers and raw capture information are available
            for investigation without interrupting the client-facing analysis.
          </p>
        </div>
      </div>
      <div class="scan-disclosure-stack">
        <details class="scan-disclosure">
          <summary>Page and run metadata</summary>
          <div class="scan-disclosure-body">
            <div class="scan-grid-two scan-detail-grid">
              <div>
                <span class="scan-subhead">Page facts</span>
                <dl class="scan-facts">
                  <Fact label="URL" value={page().url} mono />
                  <Fact label="Path" value={rawPagePath(page())} mono />
                  <Fact label="Depth" value={String(page().depth)} mono />
                  <Fact
                    label="Content length"
                    value={`${page().contentLength}`}
                    mono
                  />
                  <Fact label="Crawled" value={relativeAge(page().createdAt)} />
                </dl>
              </div>
              <div>
                <span class="scan-subhead">Run metadata</span>
                <dl class="scan-facts">
                  <Fact label="Job" value={props.value.job.id} mono />
                  <Fact label="Report" value={props.value.report.id} mono />
                  <Fact label="Job status" value={props.value.job.status} />
                  <Fact
                    label="Started"
                    value={formatDate(props.value.job.startedAt)}
                  />
                  <Fact
                    label="Finished"
                    value={formatDate(props.value.job.finishedAt)}
                  />
                  <Fact
                    label="Report pages"
                    value={`${props.value.report.pageCount}`}
                    mono
                  />
                </dl>
              </div>
            </div>
          </div>
        </details>
        <details class="scan-disclosure">
          <summary>Extracted page content</summary>
          <div class="scan-disclosure-body">
            <p class="scan-lede">
              {page().contentPreview ??
                "No extracted text is available for this page."}
            </p>
          </div>
        </details>
        <Show when={props.value.scrapeProof} keyed>
          {(proof) => (
            <details class="scan-disclosure">
              <summary>Scrape proof and raw capture information</summary>
              <div class="scan-disclosure-body">
                <ScrapeProofPanel proof={proof} />
              </div>
            </details>
          )}
        </Show>
      </div>
    </section>
  );
}
